import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useToast } from '@/hooks/use-toast';
import { useVersion } from '@/contexts/VersionContext';
import { getPlans, getVersions, getQuarterlyVersions } from '@/utils/versionUtils';
import { useGraphQLHelperV2 } from '@/utils/useGraphQLHelperV2';
import { Plan } from '../types/planTypes';

/**
 * Custom hook to manage plans data and related state
 */
export const usePlansData = (slug: string, subslug?: string | null) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actualVersion, setActualVersion] = useState<string | null>(null);
  const [allVersions, setAllVersions] = useState<string[]>([]);
  const [versionPlansMap, setVersionPlansMap] = useState<Map<string, Plan[]>>(new Map());
  const [showCurrentOnly, setShowCurrentOnly] = useState<boolean>(true);
  const [eventVersion, setEventVersion] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Get the selected version from the global context
  const { selectedVersion } = useVersion();

  // Use the global version or event version
  const effectiveGlobalVersion = selectedVersion || eventVersion || (window as any).currentGlobalVersion;

  // Format the full slug for the API call
  const fullSlug = useMemo(() => {
    if (!slug) return '';
    const cleanSubslug = subslug ? subslug.replace(/^\./,'') : '';
    const result = cleanSubslug && cleanSubslug !== 'main' ? `${slug}.${cleanSubslug}` : slug;
    console.log(`📍 [DEBUG-PLANS] Constructed fullSlug: "${result}" from slug: "${slug}" and subslug: "${subslug || 'none'}" (cleaned: "${cleanSubslug || 'none'}")`);
    return result;
  }, [slug, subslug]);

  // Get company data from Redux store
  const company = useSelector((state: any) => state.company.company);

  // Use the GraphQL helper to get more reliable data
  const { graphQLData } = useGraphQLHelperV2(slug);

  // Get quarterly versions from the GraphQL data instead of generating them
  const quarterlyVersions = useMemo(() => {
    console.log(`📍 [DEBUG-PLANS] Extracting quarterly versions using GraphQLHelper`);
    
    // First try to get quarters from the GraphQL data
    if (graphQLData && graphQLData.pages && graphQLData.pages.length > 0) {
      console.log(`📍 [DEBUG-PLANS] GraphQLData has ${graphQLData.pages.length} pages`);
      
      // Find pages with quarters
      const pagesWithQuarters = graphQLData.pages.filter(page => 
        page.quarters && Array.isArray(page.quarters) && page.quarters.length > 0
      );
      
      console.log(`📍 [DEBUG-PLANS] Found ${pagesWithQuarters.length} pages with quarters in GraphQL data`);
      
      // If we have pages with quarters, get the relevant one based on subslug
      if (pagesWithQuarters.length > 0) {
        // Consider subslug if provided
        let relevantPage = null;
        
        if (subslug) {
          const cleanInputSubslug = subslug.replace(/^\./, '');
          console.log(`📍 [DEBUG-PLANS] Looking for page with subslug matching '${cleanInputSubslug}'`);
          
          relevantPage = pagesWithQuarters.find(page => {
            const cleanPageSubslug = (page.subslug || '').replace(/^\./, '');
            return cleanPageSubslug === cleanInputSubslug;
          });
        } else {
          console.log(`📍 [DEBUG-PLANS] Looking for main page (null/empty subslug)`);
          relevantPage = pagesWithQuarters.find(page => 
            !page.subslug || page.subslug === '' || page.subslug === null
          );
        }
        
        if (relevantPage && relevantPage.quarters) {
          console.log(`📍 [DEBUG-PLANS] Found relevant page with ${relevantPage.quarters.length} quarters`);
          
          // Extract versions from quarters and sort them newest first
          const quarterVersions = relevantPage.quarters
            .map(quarter => {
              console.log(`📍 [DEBUG-PLANS] Processing quarter: ${quarter.quarter} with version: ${quarter.version}`);
              return quarter.version;
            })
            .filter(Boolean)
            .sort((a, b) => b.localeCompare(a));
            
          console.log(`📍 [DEBUG-PLANS] Using ${quarterVersions.length} quarter versions from GraphQL data: ${quarterVersions.join(', ')}`);
          return quarterVersions;
        }
      }
    }
    
    // Fallback to the old method if no quarters in GraphQL data
    console.log(`📍 [DEBUG-PLANS] Falling back to generated quarterly versions`);
    const fallbackVersions = getQuarterlyVersions(allVersions);
    console.log(`📍 [DEBUG-PLANS] Fallback versions: ${fallbackVersions.join(', ')}`);
    return fallbackVersions;
  }, [graphQLData, slug, subslug, allVersions]);

  // Listen for the custom version change event
  useEffect(() => {
    const handleVersionChange = (event: CustomEvent<{ version: string }>) => {
      const newVersion = event.detail.version;
      setEventVersion(newVersion);
    };

    window.addEventListener('versionChange', handleVersionChange);

    return () => {
      window.removeEventListener('versionChange', handleVersionChange);
    };
  }, []);

  // Fetch all available versions
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        // Get all versions for this company/page
        const versions = getVersions(subslug);
        setAllVersions(versions);
      } catch (err) {
        console.error(`❌ [CompanyPlans] Error getting versions:`, err);
      }
    };

    if (fullSlug) {
      fetchVersions();
    }
  }, [fullSlug, subslug]);

  // Fetch plans for all quarterly versions
  useEffect(() => {
    const fetchAllVersionPlans = async () => {
      setLoading(true);
      setError(null);

      try {
        // Create a map to store plans for each version
        const plansMap = new Map<string, Plan[]>();

        // First, get plans for the current selected version
        if (effectiveGlobalVersion) {
          const result = getPlans(fullSlug, effectiveGlobalVersion);

          if (result.version && result.data.length > 0) {
            setActualVersion(result.version);
            setPlans(result.data);
          } else {
            // If no plans found for selected version, try to get the most recent plans
            const latestResult = getPlans(fullSlug);
            if (latestResult.version && latestResult.data.length > 0) {
              setActualVersion(latestResult.version);
              setPlans(latestResult.data);
            } else {
              setPlans([]);
            }
          }
        }

        // Then, get plans for each quarterly version
        for (const version of quarterlyVersions) {
          const result = getPlans(fullSlug, version);
          if (result.version && result.data.length > 0) {
            plansMap.set(version, result.data);
          }
        }

        setVersionPlansMap(plansMap);
      } catch (err) {
        console.error(`❌ [CompanyPlans] Error getting plans:`, err);
        setError(`Failed to load plans data: ${err.message}`);
        toast({
          title: 'Error',
          description: `Failed to load plans data: ${err.message}`,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    if (fullSlug && quarterlyVersions.length > 0) {
      fetchAllVersionPlans();
    }
  }, [fullSlug, effectiveGlobalVersion, quarterlyVersions, toast, company]); // Added company as a dependency

  // Helper function to get plan sort value (for ordering)
  const getPlanSortValue = (plan: Plan): number => {
    // If the plan has no charges, put it at the end
    if (!plan.charges || plan.charges.length === 0) return Infinity;

    // Find the lowest price charge that's not pricing_not_disclosed
    const prices = plan.charges
      .filter(charge => charge.type !== 'pricing_not_diclosed')
      .map(charge => charge.price || 0);

    // If all charges are pricing_not_disclosed, return Infinity (to put at the end)
    if (prices.length === 0) return Infinity;

    // Otherwise, return the lowest price
    return Math.min(...prices);
  };

  // Helper function to get the most recent version that actually contains plans
  const getMostRecentVersionWithPlans = useCallback((): string | null => {
    if (quarterlyVersions.length === 0) {
      return null;
    }

    // Sort versions to ensure we're checking from newest to oldest
    const sortedVersions = [...quarterlyVersions].sort((a, b) => parseInt(b) - parseInt(a));
    
    // Find the first version that actually has plans
    for (const version of sortedVersions) {
      const versionPlans = versionPlansMap.get(version) || [];
      if (versionPlans.length > 0) {
        return version;
      }
    }
    
    // If no version has plans, fall back to the absolute latest version
    return sortedVersions[0] || null;
  }, [quarterlyVersions, versionPlansMap]);

  // Check if a plan is a legacy plan (doesn't exist in the most recent version with plans)
  const isLegacyPlan = useCallback((planId: string) => {
    if (quarterlyVersions.length === 0) {
      return false;
    }

    const mostRecentVersion = getMostRecentVersionWithPlans();
    if (!mostRecentVersion) return false;
    
    const mostRecentPlans = versionPlansMap.get(mostRecentVersion) || [];

    return !mostRecentPlans.some(recentPlan => recentPlan.id === planId);
  }, [quarterlyVersions, versionPlansMap, getMostRecentVersionWithPlans]);

  // Filter plans based on whether they exist in the most recent version with plans
  const filterPlans = useCallback((plans: Plan[]) => {
    if (!showCurrentOnly || quarterlyVersions.length === 0) {
      return plans;
    }

    const mostRecentVersion = getMostRecentVersionWithPlans();
    if (!mostRecentVersion) return plans;
    
    const mostRecentPlans = versionPlansMap.get(mostRecentVersion) || [];

    return plans.filter(plan =>
      mostRecentPlans.some(recentPlan => recentPlan.id === plan.id)
    );
  }, [showCurrentOnly, quarterlyVersions, versionPlansMap, getMostRecentVersionWithPlans]);

  // Get all unique plans across all versions
  const allUniquePlans = useMemo(() => {
    const plansSet = new Set<string>();
    const plansArray: Plan[] = [];

    // Add plans from the current version
    plans.forEach(plan => {
      if (plan.id && !plansSet.has(plan.id)) {
        plansSet.add(plan.id);
        plansArray.push(plan);
      }
    });

    // Add plans from all quarterly versions
    versionPlansMap.forEach(versionPlans => {
      versionPlans.forEach(plan => {
        if (plan.id && !plansSet.has(plan.id)) {
          plansSet.add(plan.id);
          plansArray.push(plan);
        }
      });
    });

    // Sort plans by add-on status and price
    return plansArray.sort((a, b) => {
      // First sort by add-on status
      if ((a.add_on === true) !== (b.add_on === true)) {
        return (a.add_on === true) ? 1 : -1;
      }
      // Then sort by price
      return getPlanSortValue(a) - getPlanSortValue(b);
    });
  }, [plans, versionPlansMap]);
  
  // Auto-adjust showCurrentOnly when filtered plans are empty but all plans exist
  useEffect(() => {
    const filteredPlans = filterPlans(allUniquePlans);
    if (filteredPlans.length === 0 && allUniquePlans.length > 0) {
      setShowCurrentOnly(false);
    }
  }, [allUniquePlans, showCurrentOnly, quarterlyVersions, versionPlansMap]);

  // Helper function to get all unique charges for a plan across all versions
  const getUniqueChargesForPlan = useCallback((planId: string | null | undefined) => {
    if (!planId) return [];
    
    // Get all charges for this plan from all available plans
    const allCharges: any[] = [];
    const chargeIds = new Set<string>();
    
    allUniquePlans.forEach(plan => {
      if (plan.id === planId && plan.charges && Array.isArray(plan.charges)) {
        plan.charges.forEach(charge => {
          if (charge.id && !chargeIds.has(charge.id)) {
            chargeIds.add(charge.id);
            allCharges.push(charge);
          }
        });
      }
    });
    
    return allCharges;
  }, [allUniquePlans]);
  
  // Helper function to get all unique discounts for a plan across all versions
  const getUniqueDiscountsForPlan = useCallback((planId: string | null | undefined) => {
    if (!planId) return [];
    
    // Get all discounts for this plan from all available plans
    const allDiscounts: any[] = [];
    const discountIds = new Set<string>();
    
    allUniquePlans.forEach(plan => {
      if (plan.id === planId && plan.discounts && Array.isArray(plan.discounts)) {
        plan.discounts.forEach(discount => {
          if (discount.id && !discountIds.has(discount.id)) {
            discountIds.add(discount.id);
            allDiscounts.push(discount);
          }
        });
      }
    });
    
    return allDiscounts;
  }, [allUniquePlans]);

  // Check if a plan exists in a specific version
  const isPlanInVersion = (planId: string | null | undefined, version: string): boolean => {
    if (!planId) return false;

    const versionPlans = versionPlansMap.get(version);
    return !!versionPlans?.some(p => p.id === planId);
  };

  // Function to check if a charge is in a specific version
  const isChargeInVersion = useCallback((planId: string | null | undefined, chargeId: string | null | undefined, version: string): boolean => {
    if (!planId || !chargeId) return false;
    
    // Get the map for this version
    const versionPlans = versionPlansMap.get(version);
    if (!versionPlans) return false;
    
    // Check if the plan exists in this version
    if (!isPlanInVersion(planId, version)) return false;
    
    // Check if the charge ID is in the charges array for this plan
    const planInMap = versionPlans.find(p => p.id === planId);
    if (!planInMap || !planInMap.charges) return false;
    
    // Each version has its own list of charge IDs that exist in that version
    return planInMap.charges.some(c => c.id === chargeId);
  }, [versionPlansMap, isPlanInVersion]);
  
  // Function to check if a discount is in a specific version
  const isDiscountInVersion = useCallback((planId: string | null | undefined, discountId: string | null | undefined, version: string): boolean => {
    if (!planId || !discountId) return false;
    
    // Get the map for this version
    const versionPlans = versionPlansMap.get(version);
    if (!versionPlans) return false;
    
    // Check if the plan exists in this version
    if (!isPlanInVersion(planId, version)) return false;
    
    // Check if the discount ID is in the discounts array for this plan
    const planInMap = versionPlans.find(p => p.id === planId);
    if (!planInMap || !planInMap.discounts) return false;
    
    // Each version has its own list of discount IDs that exist in that version
    return planInMap.discounts.some(d => d.id === discountId);
  }, [versionPlansMap, isPlanInVersion]);

  return {
    plans,
    loading,
    error,
    actualVersion,
    setActualVersion,
    quarterlyVersions,
    showCurrentOnly,
    setShowCurrentOnly,
    getMostRecentVersionWithPlans,
    allUniquePlans,
    filterPlans,
    getUniqueChargesForPlan,
    getUniqueDiscountsForPlan,
    isLegacyPlan,
    isPlanInVersion,
    isChargeInVersion,
    isDiscountInVersion,
    fullSlug,
    effectiveGlobalVersion,
    company,
    setPlans,
    versionPlansMap,
    setVersionPlansMap,
    allVersions
  };
};
